import { useLocation, useNavigate } from 'react-router-dom';

import { LocaleSwitcher as UiLocaleSwitcher, LOCALE_SHORT_NAMES } from '@kilat/ui';

import { useI18n, LOCALE_NATIVE_NAMES } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { switchPathnameLocale } from '../lib/locale-routing.js';
import type { Locale } from '@kilat/shared';

/**
 * Sprint 88 — thin wrapper around the shared `<LocaleSwitcher>` for marketing.
 *
 * SEO slice 2b (ADR-0024): picking a language **navigates** to that locale's
 * URL (`/pricing` → `/ms/pricing`, keeping the visitor on the same page with
 * any query preserved) instead of only flipping a client-side preference — the
 * URL is now the single source of truth for the active locale. The first-launch
 * device-detection nudge is gone with the auto-detect it depended on.
 */
export function LocaleSwitcher() {
  const { locale, supportedLocales } = useI18n();
  const brand = usePlatformBrand();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  function setLocale(next: Locale) {
    const nextPath = switchPathnameLocale(
      pathname,
      next,
      brand.supportedLocales,
      brand.defaultLocale,
    );
    navigate({ pathname: nextPath, search, hash });
  }

  return (
    <UiLocaleSwitcher<Locale>
      locale={locale}
      setLocale={setLocale}
      supportedLocales={supportedLocales}
      nativeNames={LOCALE_NATIVE_NAMES}
      shortNames={LOCALE_SHORT_NAMES}
    />
  );
}
